import { doc, getDoc, setDoc } from "firebase/firestore";
import type { CharacterProfile } from "@/character/storage";
import { loadCharacterProfile, saveCharacterProfile } from "@/character/storage";
import {
  loadEquipmentState,
  saveEquipmentState,
  type EquipmentState,
} from "@/character/equipment/storage";
import { loadSkillBarFromIdb } from "@/character/skills/skill-idb";
import { loadSkillBar, normalizeSkillBar, saveSkillBar } from "@/character/skills/storage";
import type { SkillBarState } from "@/character/skills/types";
import { isFirebaseConfigured } from "@/auth/firebase";
import { getCloudFirestore } from "@/cloud/firebase-app";
import {
  playerSaveDocId,
  PLAYER_SAVE_SECTIONS,
  type PlayerSaveSection,
} from "@/cloud/player-save-docs";
import {
  readLocalSectionTimestamps,
  writeLocalSectionTimestamps,
  type SectionTimestamps,
} from "@/cloud/player-cloud-timestamps";
import {
  loadCharacterProgression,
  normalizeProgression,
  saveCharacterProgression,
  type CharacterProgression,
} from "@/game/model/character-progression";
import {
  loadDungeonMeta,
  normalizeDungeonMeta,
  saveDungeonMeta,
  type DungeonMeta,
} from "@/dungeon/dungeon-meta";
import {
  createDefaultHomeEconomy,
  normalizeHomeEconomy,
  type HomeEconomy,
} from "@/home/economy";
import {
  loadHomeEconomyFromIdb,
  loadHomeRoomFromIdb,
  saveHomeEconomyToIdb,
  saveHomeRoomToIdb,
} from "@/home/home-idb";
import type { HomeRoomData } from "@/home/storage";
import { normalizeHomeRoom } from "@/home/storage";
import {
  loadTowerProgress,
  normalizeTowerProgress,
  saveTowerProgress,
  type TowerProgress,
} from "@/tower/tower-storage";
import { runApplyingRemotePlayerSave } from "@/cloud/player-cloud-apply-guard";
import { fitCharacterProfileForCloud } from "@/cloud/player-cloud-character-fit";
import { fitHomePayloadForCloud } from "@/cloud/player-cloud-home-fit";
import { fitSkillBarForCloud } from "@/cloud/player-cloud-skills-fit";
import {
  formatFirestoreSaveError,
  notifyPlayerCloudSaveFailures,
} from "@/cloud/player-cloud-toast";

type CloudEnvelope<T> = {
  savedAt: number;
  payload: T;
};

type PlayerGameStatePayload = {
  progression: CharacterProgression;
  equipment: EquipmentState;
  tower: TowerProgress;
  dungeonMeta: DungeonMeta;
};

type PlayerHomePayload = {
  room: HomeRoomData | null;
  economy: HomeEconomy;
};

function privateDocRef(userId: string, docId: string) {
  const db = getCloudFirestore();
  if (!db) throw new Error("Firebase ยังไม่ตั้งค่า");
  return doc(db, "users", userId, "private", docId);
}

async function loadCloudEnvelope<T>(userId: string, section: PlayerSaveSection): Promise<CloudEnvelope<T> | null> {
  try {
    const snap = await getDoc(privateDocRef(userId, playerSaveDocId(section)));
    if (!snap.exists()) return null;
    const data = snap.data();
    const savedAt = data.savedAt;
    if (typeof savedAt !== "number" || !Number.isFinite(savedAt)) return null;
    return { savedAt: Math.floor(savedAt), payload: data.payload as T };
  } catch {
    return null;
  }
}

/** Firestore rejects `undefined`; JSON round-trip drops those keys (and nested). */
function firestoreSafePayload<T>(payload: T): T {
  return JSON.parse(JSON.stringify(payload)) as T;
}

async function writeCloudEnvelope<T>(
  userId: string,
  section: PlayerSaveSection,
  payload: T,
  savedAt: number,
): Promise<void> {
  await setDoc(privateDocRef(userId, playerSaveDocId(section)), {
    savedAt,
    payload: firestoreSafePayload(payload),
  });
}

function applyCharacter(userId: string, profile: CharacterProfile): void {
  saveCharacterProfile(userId, profile);
}

function applyGameState(userId: string, payload: PlayerGameStatePayload): void {
  saveCharacterProgression(userId, normalizeProgression(payload.progression));
  saveEquipmentState(userId, payload.equipment);
  saveTowerProgress(userId, normalizeTowerProgress(payload.tower));
  saveDungeonMeta(userId, normalizeDungeonMeta(payload.dungeonMeta));
}

async function applyHome(userId: string, payload: PlayerHomePayload): Promise<void> {
  if (payload.room) {
    await saveHomeRoomToIdb(userId, normalizeHomeRoom(payload.room));
  }
  await saveHomeEconomyToIdb(userId, normalizeHomeEconomy(payload.economy));
}

async function applySkills(userId: string, bar: SkillBarState): Promise<void> {
  await saveSkillBar(userId, normalizeSkillBar(bar));
}

function localAnchorMs(userId: string): number {
  const profile = loadCharacterProfile(userId);
  if (profile && typeof profile.createdAt === "number" && profile.createdAt > 0) {
    return profile.createdAt;
  }
  return 0;
}

async function sectionHasLocalData(userId: string, section: PlayerSaveSection): Promise<boolean> {
  switch (section) {
    case "character":
      return loadCharacterProfile(userId) !== null;
    case "gameState":
      return loadCharacterProfile(userId) !== null;
    case "home": {
      const room = await loadHomeRoomFromIdb(userId);
      if (room) return true;
      return (await loadHomeEconomyFromIdb(userId)) !== null;
    }
    case "skills": {
      const raw = await loadSkillBarFromIdb(userId);
      if (!raw) return false;
      return (
        (raw.library?.length ?? 0) > 0 ||
        raw.slots?.some((id) => typeof id === "string" && id.length > 0) === true
      );
    }
  }
}

async function collectGameStatePayload(userId: string): Promise<PlayerGameStatePayload> {
  return {
    progression: loadCharacterProgression(userId),
    equipment: loadEquipmentState(userId),
    tower: loadTowerProgress(userId),
    dungeonMeta: loadDungeonMeta(userId),
  };
}

async function collectHomePayload(userId: string): Promise<PlayerHomePayload> {
  const room = await loadHomeRoomFromIdb(userId);
  const economy =
    (await loadHomeEconomyFromIdb(userId)) ?? createDefaultHomeEconomy();
  return { room, economy };
}

type PushResult = { ok: true } | { ok: false; reason: string };

async function pushSection(
  userId: string,
  section: PlayerSaveSection,
  savedAt: number,
): Promise<PushResult> {
  try {
    switch (section) {
      case "character": {
        const profile = loadCharacterProfile(userId);
        if (!profile) return { ok: false, reason: "ไม่มีตัวละคร" };
        const payload = await fitCharacterProfileForCloud(profile);
        await writeCloudEnvelope(userId, section, payload, savedAt);
        return { ok: true };
      }
      case "gameState": {
        const payload = await collectGameStatePayload(userId);
        await writeCloudEnvelope(userId, section, payload, savedAt);
        return { ok: true };
      }
      case "home": {
        const raw = await collectHomePayload(userId);
        const payload = await fitHomePayloadForCloud(
          raw.room ? normalizeHomeRoom(raw.room) : null,
          normalizeHomeEconomy(raw.economy),
        );
        await writeCloudEnvelope(userId, section, payload, savedAt);
        return { ok: true };
      }
      case "skills": {
        const bar = await loadSkillBar(userId);
        const payload = await fitSkillBarForCloud(bar);
        await writeCloudEnvelope(userId, section, payload, savedAt);
        return { ok: true };
      }
    }
  } catch (err) {
    return { ok: false, reason: formatFirestoreSaveError(err) };
  }
}

async function applyCloudSection(
  userId: string,
  section: PlayerSaveSection,
  payload: unknown,
): Promise<void> {
  await runApplyingRemotePlayerSave(async () => {
    switch (section) {
      case "character":
        applyCharacter(userId, payload as CharacterProfile);
        break;
      case "gameState":
        applyGameState(userId, payload as PlayerGameStatePayload);
        break;
      case "home":
        await applyHome(userId, payload as PlayerHomePayload);
        break;
      case "skills":
        await applySkills(userId, payload as SkillBarState);
        break;
    }
  });
}

async function shouldApplyCloudOverLocal(
  userId: string,
  section: PlayerSaveSection,
  cloudSavedAt: number,
  localTs: SectionTimestamps,
): Promise<boolean> {
  const hasLocal = await sectionHasLocalData(userId, section);
  if (!hasLocal) return true;
  const localAt = localTs[section];
  if (localAt > 0) return cloudSavedAt > localAt;
  const anchor = localAnchorMs(userId);
  return cloudSavedAt > anchor;
}

async function pullSectionFromCloud(
  userId: string,
  section: PlayerSaveSection,
  localTs: SectionTimestamps,
): Promise<void> {
  const cloud = await loadCloudEnvelope<unknown>(userId, section);
  if (!cloud) return;
  if (!(await shouldApplyCloudOverLocal(userId, section, cloud.savedAt, localTs))) {
    return;
  }
  await applyCloudSection(userId, section, cloud.payload);
  localTs[section] = cloud.savedAt;
}

async function pushSectionIfNewer(
  userId: string,
  section: PlayerSaveSection,
  localTs: SectionTimestamps,
): Promise<PushResult | null> {
  const localAt = localTs[section];
  if (localAt <= 0) return null;
  const cloud = await loadCloudEnvelope<unknown>(userId, section);
  if (cloud && cloud.savedAt >= localAt) return null;
  return pushSection(userId, section, localAt);
}

let hydrateInFlight: Promise<void> | null = null;
let hydrateUserId: string | null = null;

/** After cloud pull: mark legacy local-only sections for upload (never before pull). */
async function backfillLocalTimestampsAfterPull(
  userId: string,
  localTs: SectionTimestamps,
): Promise<void> {
  const now = Date.now();
  for (const section of PLAYER_SAVE_SECTIONS) {
    if (localTs[section] > 0) continue;
    if (!(await sectionHasLocalData(userId, section))) continue;
    const cloud = await loadCloudEnvelope<unknown>(userId, section);
    if (cloud) {
      const anchor = localAnchorMs(userId);
      if (cloud.savedAt > anchor) continue;
    }
    localTs[section] = now;
    if (section === "character" && localTs.gameState <= 0) {
      localTs.gameState = now;
    }
  }
}

/**
 * Merge cloud save with this device after sign-in. Cloud wins per section when newer.
 */
export async function ensurePlayerCloudSynced(userId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;
  if (hydrateInFlight && hydrateUserId === userId) {
    await hydrateInFlight;
    return;
  }
  hydrateUserId = userId;
  hydrateInFlight = (async () => {
    const localTs = readLocalSectionTimestamps(userId);
    for (const section of PLAYER_SAVE_SECTIONS) {
      await pullSectionFromCloud(userId, section, localTs);
    }
    await backfillLocalTimestampsAfterPull(userId, localTs);
    writeLocalSectionTimestamps(userId, localTs);
    const failed: PlayerSaveSection[] = [];
    let failDetail: string | undefined;
    for (const section of PLAYER_SAVE_SECTIONS) {
      const result = await pushSectionIfNewer(userId, section, localTs);
      if (result && !result.ok) {
        failed.push(section);
        failDetail = result.reason;
      }
    }
    if (failed.length > 0) {
      notifyPlayerCloudSaveFailures(failed, failDetail);
    }
  })();
  try {
    await hydrateInFlight;
  } finally {
    hydrateInFlight = null;
    hydrateUserId = null;
  }
}

export async function flushPlayerCloudPush(userId: string): Promise<boolean> {
  if (!isFirebaseConfigured()) return true;
  const localTs = readLocalSectionTimestamps(userId);
  const failed: PlayerSaveSection[] = [];
  let failDetail: string | undefined;
  for (const section of PLAYER_SAVE_SECTIONS) {
    if (localTs[section] <= 0) continue;
    const cloud = await loadCloudEnvelope<unknown>(userId, section);
    if (cloud && cloud.savedAt >= localTs[section]) continue;
    const result = await pushSection(userId, section, localTs[section]);
    if (!result.ok) {
      failed.push(section);
      failDetail = result.reason;
    }
  }
  if (failed.length > 0) {
    notifyPlayerCloudSaveFailures(failed, failDetail);
    return false;
  }
  return true;
}

/** One-shot upload after first character create when cloud is empty. */
export async function seedPlayerCloudFromLocal(userId: string): Promise<void> {
  if (!isFirebaseConfigured()) return;
  const profile = loadCharacterProfile(userId);
  if (!profile) return;
  const now = Date.now();
  const ts = readLocalSectionTimestamps(userId);
  if (ts.character <= 0) ts.character = now;
  if (ts.gameState <= 0) ts.gameState = now;
  writeLocalSectionTimestamps(userId, ts);
  await flushPlayerCloudPush(userId);
}
